import { store } from '../../../../app/store';
import { extractErrorMessage } from '../../../../utils/errorUtils';
import { financeEmailHistoryActionApi } from '../../api/financeEmailHistoryActionApi';
import { isInboundFinanceEmailHistoryId } from '../../utils/financeEmailHistoryId';
import type { CommandHandler } from '../types';

const getSelectedHistoryIds = (selectedRows?: Set<string>): string[] =>
  Array.from(selectedRows ?? []).filter((id) => id && !id.startsWith('temp_'));

type MailboxEndpoint =
  | 'softDeleteFinanceEmails'
  | 'restoreFinanceEmails'
  | 'markFinanceEmailsSpam'
  | 'unmarkFinanceEmailsSpam';

const runMailboxAction = async (
  context: Parameters<CommandHandler>[1],
  endpoint: MailboxEndpoint,
  confirmMessage: string
) => {
  const historyIds = getSelectedHistoryIds(context.selectedRows);
  if (historyIds.length === 0) {
    return { success: false, error: 'Please choose at least one row' };
  }

  if (context.requestConfirmation) {
    const confirmed = await context.requestConfirmation(confirmMessage);
    if (!confirmed) {
      return { success: false };
    }
  }

  try {
    const message = await store
      .dispatch(financeEmailHistoryActionApi.endpoints[endpoint].initiate(historyIds))
      .unwrap();

    context.clearSelection?.();
    return {
      success: true,
      shouldRefresh: true,
      message,
    };
  } catch (error) {
    return {
      success: false,
      error: extractErrorMessage(error, 'Action failed.'),
    };
  }
};

export const deleteFinanceEmailCommandHandler: CommandHandler = async (_command, context) => {
  const count = getSelectedHistoryIds(context.selectedRows).length;
  return runMailboxAction(
    context,
    'softDeleteFinanceEmails',
    `Move ${count} email${count > 1 ? 's' : ''} to archive?`
  );
};

export const restoreFinanceEmailCommandHandler: CommandHandler = async (_command, context) => {
  const count = getSelectedHistoryIds(context.selectedRows).length;
  return runMailboxAction(
    context,
    'restoreFinanceEmails',
    `Restore ${count} email${count > 1 ? 's' : ''} from archive?`
  );
};

export const markFinanceEmailSpamCommandHandler: CommandHandler = async (_command, context) => {
  const historyIds = getSelectedHistoryIds(context.selectedRows);
  if (historyIds.some((id) => !isInboundFinanceEmailHistoryId(id))) {
    return {
      success: false,
      error: 'Only inbound emails can be moved to spam.',
    };
  }

  const count = historyIds.length;
  return runMailboxAction(
    context,
    'markFinanceEmailsSpam',
    `Move ${count} email${count > 1 ? 's' : ''} to spam?`
  );
};

export const unmarkFinanceEmailSpamCommandHandler: CommandHandler = async (_command, context) => {
  const historyIds = getSelectedHistoryIds(context.selectedRows);
  if (historyIds.some((id) => !isInboundFinanceEmailHistoryId(id))) {
    return {
      success: false,
      error: 'Only inbound emails can be removed from spam.',
    };
  }

  const count = historyIds.length;
  return runMailboxAction(
    context,
    'unmarkFinanceEmailsSpam',
    `Remove ${count} email${count > 1 ? 's' : ''} from spam?`
  );
};
